import React, { useEffect, useState } from "react";
import { api, formatIDR, errMsg } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Upload } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import ImportProductsDialog from "@/components/ImportProductsDialog";
import { useTableFilter } from "@/lib/useTableFilter";
import { SortHeader, TableToolbar } from "@/components/TableTools";

const empty = { name: "", sku: "", unit: "pcs", pack_size: 1, stock: 0, purchase_price: 0, retail_price: 0 };

export default function Products() {
  const { isOwner } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const tf = useTableFilter(items, {
    searchKeys: ["name", "sku", "unit"],
    defaultSort: { key: "name", dir: "asc" },
  });

  const load = () => api.get("/products").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...p }); setOpen(true); };

  const save = async () => {
    try {
      const payload = { ...form, pack_size: Number(form.pack_size) || 1, stock: Number(form.stock), purchase_price: Number(form.purchase_price), retail_price: Number(form.retail_price) };
      if (editing) await api.put(`/products/${editing.id}`, payload);
      else await api.post("/products", payload);
      toast.success("Produk tersimpan");
      setOpen(false); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Hapus produk ini?")) return;
    try { await api.delete(`/products/${id}`); toast.success("Dihapus"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const filtered = tf.rows;

  return (
    <>
      <PageHeader
        title="Produk & Stok"
        subtitle="Master produk yang otomatis terupdate saat pembelian & penjualan"
        testId="page-products"
        actions={isOwner && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={()=>setImportOpen(true)} data-testid="btn-import-products">
              <Upload className="w-4 h-4 mr-2"/> Impor CSV/Excel
            </Button>
            <Button onClick={openNew} data-testid="btn-add-product" className="bg-[var(--hotto-primary)] hover:bg-[var(--hotto-primary-hover)]">
              <Plus className="w-4 h-4 mr-2"/> Produk Baru
            </Button>
          </div>
        )}
      />

      <Card className="p-4 border border-[var(--hotto-border)] rounded-md mb-4">
        <TableToolbar tf={tf} placeholder="Cari produk atau SKU..." testId="input-search-product"/>
      </Card>

      <Card className="border border-[var(--hotto-border)] rounded-md overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="w-10">#</TableHead>
              <SortHeader tf={tf} col="name">Nama</SortHeader>
              <SortHeader tf={tf} col="sku">SKU</SortHeader>
              <SortHeader tf={tf} col="unit">Satuan</SortHeader>
              <SortHeader tf={tf} col="pack_size" className="text-right">Isi/Karton</SortHeader>
              <SortHeader tf={tf} col="stock" className="text-right">Stok</SortHeader>
              <SortHeader tf={tf} col="purchase_price" className="text-right">Harga Beli</SortHeader>
              <SortHeader tf={tf} col="retail_price" className="text-right">Harga Jual</SortHeader>
              {isOwner && <TableHead className="w-24"></TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={9} className="text-center text-slate-500 py-8">Belum ada produk.</TableCell></TableRow>
            )}
            {filtered.map((p, idx) => (
              <TableRow key={p.id} className="table-row-hover" data-testid={`row-product-${p.id}`}>
                <TableCell className="font-mono-num text-slate-400 text-sm">{idx + 1}</TableCell>
                <TableCell className="font-medium">{p.name}</TableCell>
                <TableCell className="text-slate-500 font-mono-num text-xs">{p.sku || "-"}</TableCell>
                <TableCell>{p.unit}</TableCell>
                <TableCell className="text-right font-mono-num text-slate-600">{p.pack_size || 1} {p.unit}/karton</TableCell>
                <TableCell className="text-right font-mono-num">
                  {p.stock <= 5 ? (
                    <Badge variant="outline" className="border-[var(--hotto-alert)] text-[var(--hotto-alert)]">{p.stock}</Badge>
                  ) : p.stock}
                </TableCell>
                <TableCell className="text-right font-mono-num">{formatIDR(p.purchase_price)}</TableCell>
                <TableCell className="text-right font-mono-num">{formatIDR(p.retail_price)}</TableCell>
                {isOwner && (
                  <TableCell className="text-right">
                    <Button size="icon" variant="ghost" onClick={() => openEdit(p)} data-testid={`btn-edit-${p.id}`}><Pencil className="w-4 h-4"/></Button>
                    <Button size="icon" variant="ghost" onClick={() => del(p.id)} data-testid={`btn-delete-${p.id}`}><Trash2 className="w-4 h-4 text-red-500"/></Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="dialog-product">
          <DialogHeader><DialogTitle>{editing ? "Edit Produk" : "Produk Baru"}</DialogTitle></DialogHeader>          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2"><Label>Nama Produk</Label><Input value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} data-testid="input-product-name"/></div>
            <div><Label>SKU</Label><Input value={form.sku} onChange={(e)=>setForm({...form,sku:e.target.value})}/></div>
            <div><Label>Satuan Dasar (pcs/pouch/botol)</Label><Input value={form.unit} onChange={(e)=>setForm({...form,unit:e.target.value})}/></div>
            <div><Label>Isi per Karton</Label><Input type="number" value={form.pack_size} onChange={(e)=>setForm({...form,pack_size:e.target.value})} placeholder="mis. 24" data-testid="input-pack-size"/></div>
            <div>
              <Label>{editing ? "Stok Saat Ini" : "Stok Awal"} (dalam {form.unit})</Label>
              <Input type="number" value={form.stock} onChange={(e)=>setForm({...form,stock:e.target.value})} data-testid="input-product-stock"/>
              <p className="text-[11px] text-slate-500 mt-1">Stok ini bisa dipakai untuk penjualan langsung (POS) maupun dititipkan ke retail.</p>
            </div>
            <div><Label>Harga Beli / {form.unit}</Label><Input type="number" value={form.purchase_price} onChange={(e)=>setForm({...form,purchase_price:e.target.value})} data-testid="input-purchase-price"/></div>
            <div>
              <Label>Harga Beli / Karton {Number(form.pack_size)>1 && <span className="text-xs text-slate-400">(× {form.pack_size})</span>}</Label>
              <Input
                type="number"
                value={Math.round(Number(form.purchase_price || 0) * Number(form.pack_size || 1))}
                onChange={(e)=>{
                  const pack = Number(form.pack_size || 1);
                  const perUnit = pack > 0 ? Number(e.target.value) / pack : Number(e.target.value);
                  setForm({...form, purchase_price: perUnit});
                }}
                disabled={Number(form.pack_size||1) <= 1}
                data-testid="input-purchase-price-carton"
              />
            </div>
            <div className="col-span-2"><Label>Harga Jual (konsumen) / {form.unit}</Label><Input type="number" value={form.retail_price} onChange={(e)=>setForm({...form,retail_price:e.target.value})}/></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpen(false)}>Batal</Button>
            <Button onClick={save} data-testid="btn-save-product" className="bg-[var(--hotto-primary)] hover:bg-[var(--hotto-primary-hover)]">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ImportProductsDialog open={importOpen} onOpenChange={setImportOpen} onDone={load}/>
    </>
  );
}
